import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../api";
import { PageHeader, Card, Btn, Input, Field, Badge, Spinner, Empty, RequireTenant } from "../ui";
import { useAuth, usePermissions } from "../AuthContext";
import { Send, Plus, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = { draft: "slate", queued: "blue", sending: "amber", paused: "amber", completed: "green" };

function CampaignModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", template_id: "", from_email: "", from_name: "", provider: "dry_run" });
  const [templates, setTemplates] = useState([]);
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/templates").then(({ data }) => setTemplates(data.items)).catch(() => {});
    api.get("/providers").then(({ data }) => setProviders(data.providers)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post("/campaigns", form); toast.success("Campanie creata"); onSaved(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const selectCls = "w-full px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 focus:border-emerald-500 outline-none text-sm";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Campanie noua</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={save} data-testid="campaign-form">
          <Field label="Nume *"><Input data-testid="campaign-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Sablon *">
            <select data-testid="campaign-template-select" required className={selectCls} value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })}>
              <option value="">Alege sablon…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Email expeditor *"><Input data-testid="campaign-from-email-input" type="email" required value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} /></Field>
          <Field label="Nume expeditor"><Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} /></Field>
          <Field label="Provider">
            <select data-testid="campaign-provider-select" className={selectCls} value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
              {providers.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <div className="flex justify-end gap-2">
            <Btn variant="outline" type="button" onClick={onClose}>Anuleaza</Btn>
            <Btn type="submit" disabled={saving} data-testid="save-campaign-button">Creeaza</Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}

function CampaignsInner() {
  const { activeTenant } = useAuth();
  const { can } = usePermissions();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/campaigns"); setItems(data.items); }
    finally { setLoading(false); }
  }, [activeTenant]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex justify-end mb-4">
        {can("campanii", "add") && <Btn onClick={() => setShowAdd(true)} data-testid="add-campaign-button"><Plus className="w-4 h-4" /> Campanie noua</Btn>}
      </div>
      {loading ? <Spinner /> : items.length === 0 ? (
        <Card><Empty icon={Send} title="Nicio campanie" hint="Creeaza o campanie pornind de la un sablon." /></Card>
      ) : (
        <div className="space-y-3" data-testid="campaigns-list">
          {items.map((c) => (
            <Card key={c.id} className="p-5 flex items-center gap-4 cursor-pointer hover:border-emerald-300 transition-colors"
              onClick={() => nav(`/campaigns/${c.id}`)} data-testid={`campaign-row-${c.id}`}>
              <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><Send className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900 truncate">{c.name}</h3>
                  <Badge color={STATUS_COLOR[c.status]}>{c.status}</Badge>
                </div>
                <p className="text-sm text-slate-500 mt-0.5">{c.from_email} · {c.provider}</p>
              </div>
              <div className="hidden sm:flex gap-5 text-center">
                <div><div className="text-lg font-semibold text-slate-900">{c.stats?.sent || 0}</div><div className="text-xs text-slate-400">trimise</div></div>
                <div><div className="text-lg font-semibold text-slate-900">{c.stats?.opened || 0}</div><div className="text-xs text-slate-400">deschise</div></div>
                <div><div className="text-lg font-semibold text-slate-900">{c.stats?.pending || 0}</div><div className="text-xs text-slate-400">in coada</div></div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-300" />
            </Card>
          ))}
        </div>
      )}
      {showAdd && <CampaignModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

export default function Campaigns() {
  return (
    <div>
      <PageHeader title="Campanii" subtitle="Creeaza, pune in coada si trimite campanii de email." />
      <RequireTenant><CampaignsInner /></RequireTenant>
    </div>
  );
}
